import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridFilterItem, GridFilterModel, GridSingleSelectColDef } from '@mui/x-data-grid';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimeField } from '@mui/x-date-pickers/DateTimeField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import type React from 'react';
import { type FC, useEffect, useState } from 'react';

/**
 * Extended column definition with custom filter properties
 * This allows columns to specify their filter behavior without hardcoding field names
 */
export type ExtendedGridColDef = GridColDef & {
  /**
   * Specifies the input type for filtering
   * - 'text-multi': Text input for comma-separated values (e.g., topics)
   * - 'select-multi': Dropdown/Autocomplete with predefined options
   */
  filterInputType?: 'text-multi' | 'select-multi';

  /**
   * Options for select-type filters
   * If not provided, will use valueOptions from GridSingleSelectColDef
   */
  filterOptions?: string[];

  /**
   * Custom label for the filter operator
   * Defaults to 'Dentro de' for multi-select, 'Contiene' for text-multi
   */
  filterOperatorLabel?: string;

  /**
   * Whether this column supports single-value operators in addition to multi-select
   * Useful for fields that can be filtered by exact match or multiple values
   */
  supportsSingleValueOperators?: boolean;
};

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  onFilterChange: (items: GridFilterItem[]) => void;
  columns: ExtendedGridColDef[];
  filterModel: GridFilterModel;
  title?: string;
}

/**
 * Determines if a column supports multi-select operations
 *
 * This function uses column properties to determine multi-select capability,
 * completely avoiding hardcoded field names.
 *
 * A column is considered multi-select if:
 * - It has filterInputType set to 'text-multi' or 'select-multi'
 * - It's a singleSelect type with valueOptions (backward compatibility)
 * - It has filterOptions defined
 *
 * @param column - The column definition
 * @returns true if the column supports multi-select
 */
const isMultiSelectColumn = (column: ExtendedGridColDef): boolean => {
  // Check for explicit filter input type
  if (column.filterInputType === 'text-multi' || column.filterInputType === 'select-multi') {
    return true;
  }

  // Check for filterOptions (indicates select-multi behavior)
  if (column.filterOptions && column.filterOptions.length > 0) {
    return true;
  }

  // Backward compatibility: singleSelect columns with valueOptions support multi-select
  if (column.type === 'singleSelect' && (column as GridSingleSelectColDef).valueOptions) {
    return true;
  }

  return false;
};

/**
 * Gets available operators for a column based on its type and properties
 *
 * This function uses column properties to determine available operators,
 * completely avoiding hardcoded field names.
 * It automatically handles:
 * - Date fields: greaterThan, lessThan
 * - Number fields: equals, notEquals, greaterThan, lessThan
 * - Boolean fields: equals, notEquals
 * - Multi-select fields: in, isEmpty, isNotEmpty (plus equals/notEquals if supportsSingleValueOperators is true)
 * - String fields: contains, equals, notEquals, startsWith, endsWith, isEmpty, isNotEmpty
 *
 * @param column - The extended column definition
 * @returns Array of operator menu items
 */
const getOptionsForOperator = (column: ExtendedGridColDef): React.ReactNode[] => {
  if (column.type === 'date') {
    return [
      <MenuItem key="greaterThan" value="greaterThan">
        Después de
      </MenuItem>,
      <MenuItem key="lessThan" value="lessThan">
        Antes de
      </MenuItem>,
      <MenuItem key="isEmpty" value="isEmpty">
        Está vacío
      </MenuItem>,
      <MenuItem key="isNotEmpty" value="isNotEmpty">
        No está vacío
      </MenuItem>,
    ];
  }

  if (column.type === 'number') {
    return [
      <MenuItem key="equals" value="equals">
        Igual a
      </MenuItem>,
      <MenuItem key="notEquals" value="notEquals">
        No es igual a
      </MenuItem>,
      <MenuItem key="greaterThan" value="greaterThan">
        Mayor a
      </MenuItem>,
      <MenuItem key="lessThan" value="lessThan">
        Menor a
      </MenuItem>,
    ];
  }

  if (column.type === 'boolean') {
    return [
      <MenuItem key="equals" value="equals">
        Igual a
      </MenuItem>,
      <MenuItem key="notEquals" value="notEquals">
        No es igual a
      </MenuItem>,
    ];
  }

  // For multi-select columns
  if (isMultiSelectColumn(column)) {
    const operatorLabel =
      column.filterOperatorLabel || (column.filterInputType === 'text-multi' ? 'Contiene' : 'Dentro de');

    const operators = [
      <MenuItem key="in" value="in">
        {operatorLabel}
      </MenuItem>,
      <MenuItem key="isEmpty" value="isEmpty">
        Está vacío
      </MenuItem>,
      <MenuItem key="isNotEmpty" value="isNotEmpty">
        No está vacío
      </MenuItem>,
    ];

    // Add single-value operators if column supports them
    if (column.supportsSingleValueOperators) {
      operators.push(
        <MenuItem key="equals" value="equals">
          Igual a
        </MenuItem>,
        <MenuItem key="notEquals" value="notEquals">
          No es igual a
        </MenuItem>
      );
    }

    return operators;
  }

  // Default string operators
  return [
    <MenuItem key="contains" value="contains">
      Contiene
    </MenuItem>,
    <MenuItem key="equals" value="equals">
      Igual a
    </MenuItem>,
    <MenuItem key="notEquals" value="notEquals">
      No es igual a
    </MenuItem>,
    <MenuItem key="startsWith" value="startsWith">
      Comienza con
    </MenuItem>,
    <MenuItem key="endsWith" value="endsWith">
      Termina con
    </MenuItem>,
    <MenuItem key="isEmpty" value="isEmpty">
      Está vacío
    </MenuItem>,
    <MenuItem key="isNotEmpty" value="isNotEmpty">
      No está vacío
    </MenuItem>,
  ];
};

const getDefaultOperator = (field: string, columns: ExtendedGridColDef[]): string => {
  const column = columns.find((c) => c.field === field);
  if (!column) {
    return '';
  }

  if (column.type === 'date') {
    return 'greaterThan';
  }
  if (column.type === 'number') {
    return 'equals';
  }
  if (column.type === 'boolean') {
    return 'equals';
  }

  // For multi-select columns, default to 'in' operator
  if (isMultiSelectColumn(column)) {
    return 'in';
  }

  return 'contains';
};

/**
 * Gets the appropriate input component for a filter item based on column type and operator
 *
 * This function uses column properties to determine the appropriate input component,
 * completely avoiding hardcoded field names.
 * It automatically handles:
 * - Boolean fields: Select dropdown with Si/No options
 * - Date fields: DateTime picker with moment.js
 * - Multi-select fields with text-multi: Text input for comma-separated values
 * - Multi-select fields with select-multi: Autocomplete for 'in' operator, Select for single values
 * - Single select fields: Uses filterOptions or valueOptions from column definition
 * - String fields: Default text input
 *
 * @param item - The filter item
 * @param columns - Array of extended column definitions
 * @param handleValueChange - Function to handle value changes
 * @returns The appropriate input component
 */
const getInputForValue = (
  item: GridFilterItem,
  columns: ExtendedGridColDef[],
  handleValueChange: (value: string) => void
) => {
  const column = columns.find((c) => c.field === item.field);
  if (!column) {
    return null;
  }

  const isDisabled = item.operator === 'isEmpty' || item.operator === 'isNotEmpty';

  // Boolean fields
  if (column.type === 'boolean') {
    return (
      <Select
        value={item.value as string}
        onChange={(e) => handleValueChange(e.target.value)}
        label="Valor"
        size="small"
      >
        <MenuItem value="true">Si</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </Select>
    );
  }

  // Date fields
  if (column.type === 'date') {
    try {
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DateTimeField
            value={moment((item.value as string) || new Date().toISOString())}
            onChange={(newValue) => {
              handleValueChange(newValue?.toISOString() || '');
            }}
            format="DD/MM/YYYY HH:mm"
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                label: 'Valor',
              },
            }}
          />
        </LocalizationProvider>
      );
    } catch (_error) {
      return (
        <TextField
          label="Valor"
          value={String(item.value) || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          disabled={isDisabled}
          fullWidth
          size="small"
        />
      );
    }
  }

  // Multi-select columns
  if (isMultiSelectColumn(column)) {
    // For text-multi input type, use TextField with comma-separated values
    if (column.filterInputType === 'text-multi') {
      return (
        <TextField
          label="Valor"
          value={item.value as string}
          onChange={(e) => handleValueChange(e.target.value)}
          size="small"
          disabled={isDisabled}
          placeholder="Ingresa valores separados por comas"
          helperText="Para múltiples valores, sepáralos con comas"
        />
      );
    }

    // Get options from column properties
    let options: string[] = [];
    let label = 'Valor';

    // Priority: filterOptions > valueOptions from singleSelect
    if (column.filterOptions && column.filterOptions.length > 0) {
      options = column.filterOptions;
      label = column.headerName || column.field;
    } else if (column.type === 'singleSelect') {
      options = ((column as GridSingleSelectColDef).valueOptions as string[]) || [];
      label = column.headerName || column.field;
    }

    // For 'in' operator, use multi-select autocomplete
    if (item.operator === 'in' && options.length > 0) {
      const valueStr = typeof item.value === 'string' ? item.value : String(item.value || '');
      const currentValue = valueStr.length > 0 ? valueStr.split(',') : [];

      return (
        <Autocomplete
          multiple
          limitTags={3}
          options={options}
          value={currentValue}
          onChange={(_, newValue) => handleValueChange(newValue.join(','))}
          disableCloseOnSelect
          disabled={isDisabled}
          size="small"
          renderInput={(params) => <TextField {...params} label={label} size="small" />}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {option}
            </li>
          )}
          getOptionLabel={(option) => option || '--desconocido--'}
          isOptionEqualToValue={(option, value) => option === value}
        />
      );
    }

    // For single-value operators, use single select
    if (options.length > 0) {
      return (
        <Select
          value={item.value as string}
          onChange={(e) => handleValueChange(e.target.value)}
          label={label}
          size="small"
          disabled={isDisabled}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      );
    }
  }

  // Default text input for string fields
  return (
    <TextField
      label="Valor"
      value={String(item.value) || ''}
      onChange={(e) => handleValueChange(e.target.value)}
      disabled={isDisabled}
      fullWidth
      size="small"
    />
  );
};

const FilterModal: FC<FilterModalProps> = ({
  open,
  onClose,
  onFilterChange,
  columns,
  filterModel,
  title = 'Filtrar',
}) => {
  const [items, setItems] = useState<GridFilterItem[]>(filterModel.items);

  useEffect(() => {
    setItems(filterModel.items);
    setItems(
      filterModel.items.map((item) => ({
        ...item,
        operator: item.operator || getDefaultOperator(item.field, columns),
      }))
    );
  }, [filterModel.items, columns]);

  const handleFieldChange = (index: number, field: string) => {
    const defaultOperator = getDefaultOperator(field, columns);
    setItems(items.map((item, i) => (i === index ? { ...item, field, operator: defaultOperator, value: '' } : item)));
  };
  const handleOperatorChange = (index: number, operator: string) => {
    setItems(
      items.map((item, i) =>
        i === index
          ? {
              ...item,
              operator,
              value:
                operator === 'isEmpty' || operator === 'isNotEmpty'
                  ? ''
                  : (item.value as string | Date | number | boolean | null),
            }
          : item
      )
    );
  };
  const handleValueChange = (index: number, value: string) => {
    setItems(items.map((item, i) => (i === index ? { ...item, value } : item)));
  };
  const addFilter = () => {
    setItems(items.concat({ field: '', operator: '', value: '' }));
  };
  const removeFilter = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const isSomeItemEmpty = items.some((item) => {
    if (item.field === '' || item.operator === '') {
      return true;
    }
    if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') {
      return false;
    }
    return item.value === '';
  });

  const getTooltipTitle = () => {
    if (items.length === 0) {
      return 'Agrega un filtro para empezar';
    }
    if (isSomeItemEmpty) {
      return 'Completa todos los campos para aplicar el filtro';
    }
    return '';
  };

  const handleApply = () => {
    if (isSomeItemEmpty) {
      return;
    }
    onFilterChange(items);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '90%',
          maxWidth: '1000px',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 2.5,
          px: 3,
          typography: 'h5',
          fontWeight: 600,
        }}
      >
        {title}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        {items.length > 0 ? (
          <Grid container sx={{ mb: 2 }}>
            <Grid size={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Campo
              </Typography>
            </Grid>
            <Grid size={2}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Operador
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Valor
              </Typography>
            </Grid>
            <Grid size={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Eliminar
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              py: 4,
            }}
          >
            Agrega un filtro para empezar
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
          {items.map((item, index) => (
            <Grid
              container
              key={`${item.field}-${index + 1}`}
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid size={3}>
                <FormControl sx={{ width: '100%' }} size="small">
                  <InputLabel id={`field-label-${index}`}>Campo</InputLabel>
                  <Select
                    labelId={`field-label-${index}`}
                    value={item.field}
                    onChange={(e) => handleFieldChange(index, e.target.value)}
                    label="Campo"
                  >
                    {columns
                      .filter((c) => c.filterable !== false)
                      .map((column) => (
                        <MenuItem key={column.field} value={column.field}>
                          {column.headerName || column.field}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={2}>
                <FormControl sx={{ width: '100%' }} size="small">
                  <InputLabel id={`operator-label-${index}`}>Operador</InputLabel>
                  <Select
                    labelId={`operator-label-${index}`}
                    value={item.operator}
                    onChange={(e) => handleOperatorChange(index, e.target.value)}
                    label="Operador"
                  >
                    {(() => {
                      const column = columns.find((c) => c.field === item.field);
                      return column ? getOptionsForOperator(column) : [];
                    })()}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={6}>
                {getInputForValue(item, columns, (value: string) => handleValueChange(index, value))}
              </Grid>
              <Grid size={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton
                  onClick={() => removeFilter(index)}
                  size="small"
                  sx={{
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.lighter',
                    },
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Box>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            startIcon={<AddIcon />}
            onClick={addFilter}
            variant="outlined"
            size="small"
            sx={{
              borderStyle: 'dashed',
              '&:hover': {
                borderStyle: 'dashed',
              },
            }}
          >
            Agregar filtro
          </Button>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>
          Cancelar
        </Button>
        <Tooltip title={getTooltipTitle()}>
          <span>
            <Button onClick={handleApply} disabled={isSomeItemEmpty} variant="contained">
              Aplicar
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;
